import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Box, Divider, Flex, Text } from "pcln-design-system";
import { useContext } from "react";
import { v4 } from "uuid";
import { ChatbotDataContext } from "../../context/ChatbotDataContext.js";
import { useRealTimeContext } from "../../hooks/useRealTimeContext.js";
import { transformSOPQName } from "../../utils/index.js";
import { getNights } from "../../utils/conversionUtils.js";
import { extractQs } from "../../utils/extractQS.js";
import { BookHotelNowButton } from "../BookHotelNowButton.js";
import { ChooseHotelRoomButton, handleChooseHotelRoomButtonClick } from "../ChooseHotelRoomButton.js";
import { HotelAISummaryMemo } from "./HotelAISummary.js";
import { Img, ListingCardWrapper } from "./HotlListingCard.styles.js";
import { buildHotelDetailURL } from "./buildHotelDetailURL.js";
import ExpressDealBadge from "./components/ExpressDealBadge/ExpressDealBadge.js";
import Header from "./components/Header/Header.js";
import Price from "./components/Price/Price.js";
import Ratings from "./components/Ratings/Ratings.js";
import ReviewSummary from "./components/ReviewSummary/ReviewSummary.js";
import Vibes from "./components/Vibes/Vibes.js";
const ReviewsAndVibesSection = ({ shouldShowReviewsAndVibes, vibes, hotelId, isReviewSummaryInContext, summarizedPositiveText, summarizedNegativeText, showThumbsDown })=>{
    if (!shouldShowReviewsAndVibes) return null;
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            vibes?.length ? /*#__PURE__*/ jsx(Vibes, {
                vibes: vibes,
                hotelId: hotelId
            }) : null,
            isReviewSummaryInContext ? /*#__PURE__*/ jsx(ReviewSummary, {
                summarizedPositiveText: summarizedPositiveText,
                summarizedNegativeText: summarizedNegativeText,
                showThumbsDown: showThumbsDown
            }) : null
        ]
    });
};
const AISummarySection = ({ shouldRenderAISummary, hotelAISummary, shouldDisplayGenHotelFeature, propertyName, isExpressDeal, fetchRequestOptions, description, starRating, hotelAmenities, petDescription })=>{
    if (!shouldRenderAISummary) return null;
    if (hotelAISummary) return /*#__PURE__*/ jsx(Text, {
        fontSize: 0,
        lineHeight: "20px",
        pb: 1,
        pt: 2,
        children: hotelAISummary
    });
    return /*#__PURE__*/ jsx(HotelAISummaryMemo, {
        shouldDisplayGenHotelFeature: shouldDisplayGenHotelFeature,
        propertyName: propertyName,
        isExpressDeal: isExpressDeal,
        fetchRequestOptions: fetchRequestOptions,
        description: description,
        starRating: starRating,
        hotelAmenities: hotelAmenities,
        petDescription: petDescription
    });
};
const HotelListingCard = ({ listingProps, hotel, args, isPricingVariant = false, reviewConfiguration, submitMessages })=>{
    const { imageSrc, headerText, ratingsProps, priceProps } = listingProps ?? {};
    const { rooms, 'check-in': checkIn, 'check-out': checkOut } = args;
    const nights = getNights(checkIn, checkOut);
    const priceAndNightsProps = {
        ...priceProps,
        rooms,
        nights
    };
    const { setiValues, fetchRequestOptions, shouldDisplayGenHotelFeature, submitChatMessagesPayload } = useContext(ChatbotDataContext);
    const { shouldShowAISummariesWithReviewsAndVibes = false, summarySectionHeight = '120px', showThumbsDown = true } = reviewConfiguration ?? {};
    const { startRealTime } = useRealTimeContext();
    const { starRating, location, pclnPropertyTypeId, hotelType, description, ratesSummary, itemKey, hotelAISummary, hotelDetailsURL, summarizedNegativeText, summarizedPositiveText, isReviewSummaryInContext, vibes } = hotel ?? {};
    const isExpressDeal = 'SOPQ' === hotelType;
    const shouldDisplayCheckoutButton = setiValues.shouldDisplayCheckoutButton;
    const shouldShowReviewsAndVibes = vibes?.length > 0;
    const transformSopqNameArgs = {
        starRating,
        location,
        pclnPropertyTypeId
    };
    const queryString = extractQs(hotelDetailsURL) ?? '';
    const propertyName = isExpressDeal && !hotel.name ? transformSOPQName(transformSopqNameArgs) : hotel.name;
    const detailURL = buildHotelDetailURL(args, hotel, queryString);
    const { priceDisplayRegulation, minCurrencyCodeSymbol, minCurrencyCode, nightlyRateIncludingTaxesAndFees, grandTotal, minPrice, priceKey } = ratesSummary ?? {};
    const hasDates = checkIn && checkOut;
    const showHotelPrice = 'TOTAL_PRICE_PROMINENT' === priceDisplayRegulation ? minCurrencyCodeSymbol && !isNaN(nightlyRateIncludingTaxesAndFees) && !isNaN(grandTotal) : minCurrencyCodeSymbol && !isNaN(minPrice) && !isNaN(grandTotal);
    const shouldRenderAISummary = !shouldShowReviewsAndVibes || shouldShowAISummariesWithReviewsAndVibes;
    return /*#__PURE__*/ jsxs(ListingCardWrapper, {
        borderRadius: 'xl',
        boxShadow: 'lg',
        onClick: handleChooseHotelRoomButtonClick({
            hotelId: hotel.hotelId,
            args,
            detailURL
        }),
        mb: '20px',
        pb: '10px',
        bg: startRealTime ? 'text.lightest' : 'null',
        children: [
            /*#__PURE__*/ jsxs(Box, {
                children: [
                    isExpressDeal && /*#__PURE__*/ jsx(ExpressDealBadge, {}),
                    /*#__PURE__*/ jsx(Img, {
                        src: imageSrc
                    })
                ]
            }),
            /*#__PURE__*/ jsxs(Box, {
                p: 2,
                pb: '0',
                children: [
                    /*#__PURE__*/ jsxs(Box, {
                        height: shouldShowReviewsAndVibes ? summarySectionHeight : '120px',
                        children: [
                            /*#__PURE__*/ jsx(Box, {
                                children: /*#__PURE__*/ jsx(Header, {
                                    ...headerText
                                })
                            }),
                            /*#__PURE__*/ jsx(ReviewsAndVibesSection, {
                                shouldShowReviewsAndVibes: shouldShowReviewsAndVibes,
                                vibes: vibes,
                                hotelId: hotel?.hotelId,
                                isReviewSummaryInContext: isReviewSummaryInContext,
                                summarizedPositiveText: summarizedPositiveText,
                                summarizedNegativeText: summarizedNegativeText,
                                showThumbsDown: showThumbsDown
                            }),
                            /*#__PURE__*/ jsx(AISummarySection, {
                                shouldRenderAISummary: shouldRenderAISummary,
                                hotelAISummary: hotelAISummary,
                                shouldDisplayGenHotelFeature: shouldDisplayGenHotelFeature,
                                propertyName: propertyName,
                                isExpressDeal: isExpressDeal,
                                fetchRequestOptions: fetchRequestOptions,
                                description: description,
                                starRating: starRating,
                                hotelAmenities: hotel?.hotelFeatures?.hotelAmenities,
                                petDescription: hotel?.policies?.petDescription
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx(Box, {
                        mt: 3,
                        height: '20px',
                        children: /*#__PURE__*/ jsxs(Box, {
                            children: [
                                /*#__PURE__*/ jsx(Ratings, {
                                    ...ratingsProps
                                }),
                                ' '
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ jsx(Divider, {
                width: 1,
                my: 1
            }),
            /*#__PURE__*/ jsx(Flex, {
                p: 0,
                px: 2,
                pb: 2,
                height: [
                    '100px',
                    '70px'
                ],
                children: showHotelPrice ? /*#__PURE__*/ jsx(Price, {
                    ...priceAndNightsProps,
                    isPricingVariant: isPricingVariant,
                    propertyName: propertyName,
                    hasDates: hasDates
                }) : null
            }),
            shouldDisplayCheckoutButton ? /*#__PURE__*/ jsxs(Flex, {
                p: 0,
                px: 2,
                mt: 2,
                children: [
                    /*#__PURE__*/ jsx(ChooseHotelRoomButton, {
                        hotelId: hotel.hotelId,
                        args: args,
                        buttonProps: {
                            variation: 'subtle',
                            width: '100%'
                        },
                        detailURL: detailURL,
                        buttonCopy: "Choose Room"
                    }),
                    /*#__PURE__*/ jsx(BookHotelNowButton, {
                        buttonProps: {
                            variation: 'fill',
                            color: 'secondary',
                            width: '100%',
                            ml: 2
                        },
                        cguid: submitChatMessagesPayload?.cguid,
                        rguid: v4(),
                        itemKey: itemKey,
                        priceKey: priceKey,
                        total: grandTotal,
                        minCurrencyCode: minCurrencyCode,
                        minCurrencyCodeSymbol: minCurrencyCodeSymbol,
                        checkIn: checkIn,
                        checkOut: checkOut,
                        hotelName: hotel.name,
                        hotelId: hotel.hotelId,
                        submitMessages: submitMessages
                    })
                ]
            }) : null
        ]
    });
};
const HotelListingCard_HotelListingCard = HotelListingCard;
export { HotelListingCard_HotelListingCard as default };
